// ─────────────────────────────────────────────────────────────────────────────
// Calculadora de avaliação de imóvel (AVM) — CONTRATO-AVM-SITE.md §5.
//
// Monta no lugar do placeholder que o builder do CRM grava no HTML da página
// (`lib/avm/placeholder.ts` faz o recorte). Quatro passos + resultado:
//   1 Onde fica · 2 Como é o imóvel · 3 Diferenciais · 4 Quer anunciar? + contato · 5 Resultado
//
// Notas de arquitetura:
//  • SSR-SAFE de propósito (nada de window/document no render): o passo 1 já sai no HTML
//    do servidor, então o bloco não "pula" nem empurra o conteúdo depois da hidratação.
//  • COR: só o que vem em `config.theme`, via custom properties no wrapper. O bloco vive
//    dentro do HTML do builder e não pode depender do CSS dele nem das cores do site.
//  • O envio passa pelo relay `/api/avaliacao` (same-origin) — nunca direto no app.
// ─────────────────────────────────────────────────────────────────────────────
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/Info';
import { allCaracteristicasCatalogo, district, get_estados } from '../../services/selectors';
import { getAccessibleTextColor } from '../../utils/FunctionsGlobals';
import { resolveSiteWhatsAppUrl } from '../../utils/site2524WhatsApp';
import type { AvmConfig, AvmResposta } from '../../lib/avm/types';
import type { AvmCatalogoCaracteristicas, AvmErros, AvmForm, AvmOpcao } from './avm.utils';
import {
    corComAlfa,
    formInicialConservacao,
    mensagemWhatsapp,
    consentimentoNoPasso3,
    montarPayload,
    separarCatalogo,
    telefoneWhatsappDoSite,
    usaCatalogoCaracteristicas,
    usaConservacao7Opcoes,
    validarPasso,
} from './avm.utils';
import { Passo1, Passo2, Passo3, Passo4 } from './AvmPassos';
import AvmResultado from './AvmResultado';
import { Icone, useIdEstavel } from './AvmUI';
import styles from './AvmCalculator.module.css';

const TOTAL_PASSOS = 4;

const TITULOS: Record<number, { titulo: string; ajuda: string }> = {
    1: { titulo: 'Onde fica o imóvel?', ajuda: 'A região é o que mais pesa no valor.' },
    2: { titulo: 'Como é o imóvel?', ajuda: 'Só o básico: tipo, tamanho e cômodos.' },
    3: { titulo: 'Diferenciais', ajuda: 'O que o imóvel tem e em que estado ele está.' },
    4: { titulo: 'Quase lá!', ajuda: 'Para onde enviamos a sua estimativa?' },
};

/** UF a partir do nome do estado — o site guarda `estados { id nome pais }`. */
const SIGLAS: Record<string, string> = {
    acre: 'AC', alagoas: 'AL', amapa: 'AP', amazonas: 'AM', bahia: 'BA', ceara: 'CE',
    'distrito federal': 'DF', 'espirito santo': 'ES', goias: 'GO', maranhao: 'MA',
    'mato grosso': 'MT', 'mato grosso do sul': 'MS', 'minas gerais': 'MG', para: 'PA',
    paraiba: 'PB', parana: 'PR', pernambuco: 'PE', piaui: 'PI', 'rio de janeiro': 'RJ',
    'rio grande do norte': 'RN', 'rio grande do sul': 'RS', rondonia: 'RO', roraima: 'RR',
    'santa catarina': 'SC', 'sao paulo': 'SP', sergipe: 'SE', tocantins: 'TO',
};

function ufDoNome(nome: string): string | null {
    const bruto = String(nome || '').trim();
    if (!bruto) return null;
    if (bruto.length === 2) return bruto.toUpperCase();
    const chave = bruto
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    return SIGLAS[chave] || null;
}

/** aceita tanto `{id, nome}` (payload do site) quanto `{value, label}` (formatSelects). */
function comoOpcoes(lista: any): AvmOpcao[] {
    if (!Array.isArray(lista)) return [];
    return lista
        .map((item) => ({
            id: Number(item?.id ?? item?.value),
            nome: String(item?.nome ?? item?.label ?? '').trim(),
        }))
        .filter((item) => Number.isFinite(item.id) && item.id > 0 && !!item.nome);
}

export interface AvmCalculatorProps {
    config: AvmConfig;
    /** `site` já com a região/filial aplicada (o mesmo que o resto da página usa). */
    site?: any;
    cidades?: any;
    tipos?: any;
}

const AvmCalculator = ({ config, site: siteProp, cidades: cidadesProp, tipos: tiposProp }: AvmCalculatorProps) => {
    const { allDataHome, init_props } = useAuth();
    const site = siteProp || allDataHome?.site;
    const idBase = useIdEstavel('avm');

    const codsite = Number(config.codsite || init_props?.codsite || 0);
    const origem = init_props?.origemInit;
    // cs2514 (21/09): as 7 opções não vêm com nada pré-marcado — a escolha é obrigatória.
    const comConservacao7 = usaConservacao7Opcoes(codsite);
    const formInicial = useMemo(() => formInicialConservacao(codsite), [codsite]);

    const cidades = useMemo(() => comoOpcoes(cidadesProp || allDataHome?.cidades), [cidadesProp, allDataHome?.cidades]);
    const tipos = useMemo(() => comoOpcoes(tiposProp || allDataHome?.tipos), [tiposProp, allDataHome?.tipos]);

    const [passo, setPasso] = useState(1);
    const [form, setForm] = useState<AvmForm>(formInicial);
    const [erros, setErros] = useState<AvmErros>({});
    const [enviando, setEnviando] = useState(false);
    const [erroEnvio, setErroEnvio] = useState<string | null>(null);
    const [resposta, setResposta] = useState<AvmResposta | null>(null);

    const [bairros, setBairros] = useState<AvmOpcao[]>([]);
    const [carregandoBairros, setCarregandoBairros] = useState(false);
    const [ufPorEstado, setUfPorEstado] = useState<Record<string, string> | null>(null);
    const [reduzido, setReduzido] = useState(false);

    const utmsRef = useRef<Record<string, string | null>>({});
    const cardRef = useRef<HTMLDivElement | null>(null);
    const primeiroRenderRef = useRef(true);
    // Espelho SÍNCRONO do formulário: o blur do combo valida 140 ms depois de escolher a
    // opção (e o state do React ainda não chegou lá) — sem isto, escolher a cidade e sair
    // do campo acusava "Escolha a cidade do imóvel".
    const formRef = useRef<AvmForm>(formInicial);

    // Catálogo do CRM no passo 3 (só nos sites com gate): null = lista fixa de sempre.
    const comCatalogo = usaCatalogoCaracteristicas(codsite);
    // Autorização no passo 3 (abaixo do "Estado de conservação") nos sites com gate; senão no 4.
    const consentNoPasso3 = consentimentoNoPasso3(codsite);
    const [catalogo, setCatalogo] = useState<AvmCatalogoCaracteristicas | null>(null);
    const [carregandoCatalogo, setCarregandoCatalogo] = useState(comCatalogo);

    /* ── ambiente (só depois da montagem: o render continua SSR-safe) ────────── */

    useEffect(() => {
        try {
            const q = new URLSearchParams(window.location.search);
            utmsRef.current = {
                utm_source: q.get('utm_source'),
                utm_medium: q.get('utm_medium'),
                utm_campaign: q.get('utm_campaign'),
                utm_id: q.get('utm_id'),
            };
        } catch {
            utmsRef.current = {};
        }

        const mq = typeof window.matchMedia === 'function'
            ? window.matchMedia('(prefers-reduced-motion: reduce)')
            : null;
        if (!mq) return;
        const aplicar = () => setReduzido(!!mq.matches);
        aplicar();
        if (typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', aplicar);
            return () => mq.removeEventListener('change', aplicar);
        }
        if (typeof (mq as any).addListener === 'function') {
            (mq as any).addListener(aplicar);
            return () => (mq as any).removeListener(aplicar);
        }
        return;
    }, []);

    /* ── catálogo de características do CRM (unidade × condomínio) ─────────── */

    useEffect(() => {
        if (!comCatalogo) return;
        let ativo = true;
        setCarregandoCatalogo(true);
        allCaracteristicasCatalogo(origem, codsite)
            .then((dados: any) => {
                if (!ativo) return;
                const separado = separarCatalogo(dados?.caracteristicas);
                // catálogo vazio ou falha → o passo 3 volta para a lista fixa (nunca fica em branco)
                setCatalogo(separado.unidade.length || separado.condominio.length ? separado : null);
            })
            .catch(() => {
                if (ativo) setCatalogo(null);
            })
            .finally(() => {
                if (ativo) setCarregandoCatalogo(false);
            });
        return () => {
            ativo = false;
        };
    }, [comCatalogo, origem, codsite]);

    /* ── bairros da cidade escolhida (mesma fonte da busca do site) ──────────── */

    useEffect(() => {
        const idCidade = Number(form.cidadeId);
        if (!idCidade) {
            setBairros([]);
            return;
        }
        let ativo = true;
        setCarregandoBairros(true);
        district(idCidade, origem, codsite)
            .then((dados: any) => {
                if (!ativo) return;
                setBairros(comoOpcoes(dados?.bairros));
            })
            .catch(() => {
                // sem lista, o campo de bairro vira texto livre (ver Passo1)
                if (ativo) setBairros([]);
            })
            .finally(() => {
                if (ativo) setCarregandoBairros(false);
            });
        return () => {
            ativo = false;
        };
    }, [form.cidadeId, origem, codsite]);

    /* ── UF da cidade (lazy: só depois que o visitante escolhe a cidade) ─────── */

    useEffect(() => {
        if (!form.cidadeId || ufPorEstado) return;
        let ativo = true;
        get_estados(origem, codsite)
            .then((dados: any) => {
                if (!ativo) return;
                const mapa: Record<string, string> = {};
                for (const estado of Array.isArray(dados?.estados) ? dados.estados : []) {
                    const uf = ufDoNome(estado?.nome);
                    if (estado?.id != null && uf) mapa[String(estado.id)] = uf;
                }
                setUfPorEstado(mapa);
            })
            .catch(() => {
                // sem estados o payload vai com uf: null — o app resolve pelo cidadeId
                if (ativo) setUfPorEstado({});
            });
        return () => {
            ativo = false;
        };
    }, [form.cidadeId, ufPorEstado, origem, codsite]);

    const ufAtual = useMemo(() => {
        if (!ufPorEstado || !form.cidadeId) return null;
        const lista: any[] = Array.isArray(cidadesProp || allDataHome?.cidades)
            ? (cidadesProp || allDataHome?.cidades)
            : [];
        const cidade = lista.find((c: any) => String(c?.id) === form.cidadeId);
        const idEstado = cidade?.id_estado;
        return idEstado != null ? ufPorEstado[String(idEstado)] || null : null;
    }, [ufPorEstado, form.cidadeId, cidadesProp, allDataHome?.cidades]);

    /* ── estado do formulário ───────────────────────────────────────────────── */

    const setCampo = useCallback(<K extends keyof AvmForm>(campo: K, valor: AvmForm[K]) => {
        if (formRef.current[campo] === valor) return;
        formRef.current = { ...formRef.current, [campo]: valor };
        setForm(formRef.current);
        setErros((atuais) => {
            if (!atuais[campo]) return atuais;
            const novos = { ...atuais };
            delete novos[campo];
            return novos;
        });
    }, []);

    /** validação inline: no blur, só o erro DAQUELE campo aparece. */
    const aoSairDoCampo = useCallback(
        (campo: keyof AvmForm) => {
            const doPasso = validarPasso(passo, formRef.current, { consentNoPasso3, conservacaoObrigatoria: comConservacao7 });
            setErros((atuais) => {
                const novos = { ...atuais };
                if (doPasso[campo]) novos[campo] = doPasso[campo] as string;
                else delete novos[campo];
                return novos;
            });
        },
        [passo, consentNoPasso3, comConservacao7],
    );

    const focarPrimeiroErro = useCallback(
        (encontrados: AvmErros) => {
            if (typeof document === 'undefined') return;
            const campo = Object.keys(encontrados)[0];
            if (!campo) return;
            const sufixo = campo === 'cidadeId' ? 'cidade' : campo === 'bairroNome' ? 'bairro' : campo;
            const alvo =
                document.getElementById(`${idBase}-${sufixo}`) ||
                (document.getElementById(`${idBase}-${campo}-grupo`)?.querySelector('button') as HTMLElement | null);
            if (alvo && typeof alvo.focus === 'function') {
                alvo.focus({ preventScroll: false } as any);
            }
        },
        [idBase],
    );

    const irPara = useCallback(
        (proximo: number) => {
            setPasso(proximo);
            setErroEnvio(null);
        },
        [],
    );

    const continuar = useCallback(() => {
        const encontrados = validarPasso(passo, formRef.current, { consentNoPasso3, conservacaoObrigatoria: comConservacao7 });
        setErros(encontrados);
        if (Object.keys(encontrados).length) {
            focarPrimeiroErro(encontrados);
            return;
        }
        irPara(Math.min(TOTAL_PASSOS, passo + 1));
    }, [passo, consentNoPasso3, comConservacao7, focarPrimeiroErro, irPara]);

    const enviar = useCallback(async () => {
        const atual = formRef.current;
        const encontrados = validarPasso(4, atual, { consentNoPasso3 });
        setErros(encontrados);
        if (Object.keys(encontrados).length) {
            focarPrimeiroErro(encontrados);
            return;
        }

        setEnviando(true);
        setErroEnvio(null);
        try {
            const res = await fetch('/api/avaliacao', {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(
                    montarPayload({
                        form: atual,
                        codsite,
                        token: config.token,
                        uf: ufAtual,
                        utms: utmsRef.current,
                    }),
                ),
            });
            const json = (await res.json().catch(() => null)) as AvmResposta | null;

            if (!res.ok || !json) {
                // 502/503 (app fora, rate limit) ou corpo estranho: os dados ficam na tela
                // e o visitante tenta de novo — nada de mandar ele para um resultado vazio.
                setErroEnvio(
                    (json && typeof (json as any).mensagem === 'string' && (json as any).mensagem) ||
                        'Não conseguimos calcular agora. Tente de novo em instantes.',
                );
                return;
            }

            setResposta(json);
            setPasso(5);
        } catch {
            setErroEnvio('Sem conexão com o servidor. Confira sua internet e tente de novo.');
        } finally {
            setEnviando(false);
        }
    }, [codsite, config.token, ufAtual, consentNoPasso3, focarPrimeiroErro]);

    const reiniciar = useCallback(() => {
        formRef.current = formInicial;
        setForm(formInicial);
        setErros({});
        setResposta(null);
        setErroEnvio(null);
        setPasso(1);
    }, [formInicial]);

    /* ── rolagem suave ao trocar de passo (nunca no primeiro render) ─────────── */

    useEffect(() => {
        if (primeiroRenderRef.current) {
            primeiroRenderRef.current = false;
            return;
        }
        const card = cardRef.current;
        if (!card || typeof card.getBoundingClientRect !== 'function') return;
        // só puxa a tela quando o topo do card saiu de vista — senão o visitante
        // levaria um "pulo" a cada passo (ver reference_next_focus_no_mount_rola_a_pagina).
        if (card.getBoundingClientRect().top >= 0) return;
        try {
            card.scrollIntoView({ behavior: reduzido ? 'auto' : 'smooth', block: 'start' });
        } catch {
            card.scrollIntoView();
        }
    }, [passo, reduzido]);

    /* ── tema ───────────────────────────────────────────────────────────────── */

    const estilo = useMemo(() => {
        const t = config.theme;
        const vars: Record<string, string> = {
            '--avm-primary': t.primary,
            '--avm-secondary': t.secondary,
            '--avm-bg': t.bg,
            '--avm-text': t.text,
            '--avm-surface': t.surface,
            '--avm-muted': t.muted,
            '--avm-on-primary': getAccessibleTextColor(t.primary),
            '--avm-on-secondary': getAccessibleTextColor(t.secondary),
            '--avm-primary-suave': corComAlfa(t.primary, 0.08),
            '--avm-secondary-suave': corComAlfa(t.secondary, 0.14),
            '--avm-anel': corComAlfa(t.primary, 0.22),
            '--avm-anel-forte': corComAlfa(t.primary, 0.45),
        };
        return vars as unknown as React.CSSProperties;
    }, [config.theme]);

    const hrefWhatsapp = useMemo(() => {
        const telefone = telefoneWhatsappDoSite(site);
        if (!telefone) return null;
        const valor = resposta && resposta.ok ? resposta.valorSugerido : null;
        const prefixo = telefone.length > 11 ? '' : '55';
        const url = `https://api.whatsapp.com/send?phone=${prefixo}${telefone}&text=${encodeURIComponent(
            mensagemWhatsapp(valor),
        )}`;
        return resolveSiteWhatsAppUrl(site, url);
    }, [site, resposta]);

    /* ── render ─────────────────────────────────────────────────────────────── */

    const animacao = reduzido
        ? { initial: false as const, animate: { opacity: 1 }, exit: { opacity: 1 }, transition: { duration: 0 } }
        : {
              initial: { opacity: 0, x: 24 },
              animate: { opacity: 1, x: 0 },
              exit: { opacity: 0, x: -24 },
              transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
          };

    const cabecalhoPasso = TITULOS[passo];

    return (
        <section className={styles.wrapper} style={estilo} aria-label={config.titulo}>
            <div className={styles.card} ref={cardRef}>
                <header className={styles.cabecalho}>
                    <h2 className={styles.titulo}>{config.titulo}</h2>
                    <p className={styles.subtitulo}>{config.subtitulo}</p>
                    {passo <= TOTAL_PASSOS ? (
                        <div className={styles.progresso}>
                            <div
                                className={styles.progressoTrilho}
                                role="progressbar"
                                aria-valuemin={1}
                                aria-valuemax={TOTAL_PASSOS}
                                aria-valuenow={passo}
                                aria-label={`Passo ${passo} de ${TOTAL_PASSOS}`}
                            >
                                <div
                                    className={styles.progressoPreenchido}
                                    style={{ width: `${(passo / TOTAL_PASSOS) * 100}%` }}
                                />
                            </div>
                            <span className={styles.progressoTexto}>
                                Passo {passo} de {TOTAL_PASSOS}
                            </span>
                        </div>
                    ) : null}
                </header>

                <div className={styles.corpo}>
                    <form
                        noValidate
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (enviando) return;
                            if (passo === TOTAL_PASSOS) enviar();
                            else continuar();
                        }}
                        onKeyDown={(e) => {
                            // Enter avança/envia (o combo aberto intercepta antes — ver AvmUI).
                            if (e.key !== 'Enter') return;
                            const alvo = e.target as HTMLElement | null;
                            if (alvo && (alvo.tagName === 'TEXTAREA' || alvo.tagName === 'BUTTON')) return;
                            e.preventDefault();
                            if (enviando) return;
                            if (passo === TOTAL_PASSOS) enviar();
                            else continuar();
                        }}
                    >
                        {/* honeypot: fora da tela e fora da navegação por teclado (§5) */}
                        <div className={styles.honeypot} aria-hidden="true">
                            <label htmlFor={`${idBase}-hp`}>Não preencha este campo</label>
                            <input
                                id={`${idBase}-hp`}
                                name="__hp"
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                value={form.__hp}
                                onChange={(e) => setCampo('__hp', e.target.value)}
                            />
                        </div>

                        {passo <= TOTAL_PASSOS && cabecalhoPasso ? (
                            <>
                                <h3 className={styles.passoTitulo}>{cabecalhoPasso.titulo}</h3>
                                <p className={styles.passoAjuda}>{cabecalhoPasso.ajuda}</p>
                            </>
                        ) : null}

                        <AnimatePresence mode="wait" initial={false}>
                            <motion.div key={passo} {...animacao}>
                                {passo === 1 ? (
                                    <Passo1
                                        form={form}
                                        erros={erros}
                                        travado={enviando}
                                        idBase={idBase}
                                        setCampo={setCampo}
                                        aoSairDoCampo={aoSairDoCampo}
                                        cidades={cidades}
                                        bairros={bairros}
                                        carregandoBairros={carregandoBairros}
                                    />
                                ) : null}
                                {passo === 2 ? (
                                    <Passo2
                                        form={form}
                                        erros={erros}
                                        travado={enviando}
                                        idBase={idBase}
                                        setCampo={setCampo}
                                        aoSairDoCampo={aoSairDoCampo}
                                        tipos={tipos}
                                    />
                                ) : null}
                                {passo === 3 ? (
                                    <Passo3
                                        form={form}
                                        erros={erros}
                                        travado={enviando}
                                        idBase={idBase}
                                        setCampo={setCampo}
                                        aoSairDoCampo={aoSairDoCampo}
                                        catalogo={catalogo}
                                        carregandoCatalogo={comCatalogo && carregandoCatalogo}
                                        comConservacao7={comConservacao7}
                                        consentNoPasso3={consentNoPasso3}
                                        config={config}
                                    />
                                ) : null}
                                {passo === 4 ? (
                                    <Passo4
                                        form={form}
                                        erros={erros}
                                        travado={enviando}
                                        idBase={idBase}
                                        setCampo={setCampo}
                                        aoSairDoCampo={aoSairDoCampo}
                                        config={config}
                                        consentNoPasso3={consentNoPasso3}
                                    />
                                ) : null}
                                {passo === 5 && resposta ? (
                                    <AvmResultado
                                        config={config}
                                        resposta={resposta}
                                        hrefWhatsapp={hrefWhatsapp}
                                        aoReiniciar={reiniciar}
                                        aoRevisar={() => {
                                            setResposta(null);
                                            setPasso(1);
                                        }}
                                    />
                                ) : null}
                            </motion.div>
                        </AnimatePresence>

                        {erroEnvio ? (
                            <div className={styles.avisoErro} role="alert">
                                <Icone nome="alerta" tamanho={18} trecho={2} />
                                <span>{erroEnvio}</span>
                            </div>
                        ) : null}

                        {passo <= TOTAL_PASSOS ? (
                            <div className={styles.rodape}>
                                {passo > 1 ? (
                                    <button
                                        type="button"
                                        className={`${styles.botao} ${styles.botaoSecundario}`}
                                        disabled={enviando}
                                        onClick={() => irPara(passo - 1)}
                                    >
                                        Voltar
                                    </button>
                                ) : null}
                                <button type="submit" className={`${styles.botao} ${styles.botaoPrimario}`} disabled={enviando}>
                                    {enviando ? (
                                        <>
                                            <span className={styles.spinner} aria-hidden="true" />
                                            Calculando…
                                        </>
                                    ) : passo === TOTAL_PASSOS ? (
                                        erroEnvio ? 'Tentar de novo' : 'Ver a estimativa'
                                    ) : (
                                        'Continuar'
                                    )}
                                </button>
                            </div>
                        ) : null}
                    </form>
                </div>
            </div>
        </section>
    );
};

export default AvmCalculator;
